<template>
  <div class="p-4 md:p-6">
    <!-- Header con switch vista -->
    <div class="flex flex-col md:flex-row md:items-center md:justify-between mb-5 gap-3">
      <div>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white">Utenti a rischio</h2>
        <p class="text-sm text-gray-500 mt-0.5">
          {{ activeView === 'quick'
            ? 'Lista rapida — utenti con segnali sospetti (carta condivisa, non pagato, profilo estero, email anomala)'
            : 'Analisi approfondita — ogni utente ha uno score calcolato sui segnali combinati'
          }}
        </p>
      </div>
      <div class="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
        <button
          class="px-3 py-1.5 rounded-md text-sm font-medium transition"
          :class="activeView === 'quick'
            ? 'bg-white dark:bg-gray-700 shadow text-gray-900 dark:text-white'
            : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'"
          @click="activeView = 'quick'"
        >
          <span class="material-icons text-sm align-middle mr-1">bolt</span>
          Lista rapida
        </button>
        <button
          class="px-3 py-1.5 rounded-md text-sm font-medium transition"
          :class="activeView === 'deep'
            ? 'bg-white dark:bg-gray-700 shadow text-gray-900 dark:text-white'
            : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'"
          @click="switchToDeep"
        >
          <span class="material-icons text-sm align-middle mr-1">psychology</span>
          Analisi approfondita
        </button>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════════════════ -->
    <!-- VISTA QUICK (lista leggera)                                -->
    <!-- ═══════════════════════════════════════════════════════════ -->
    <template v-if="activeView === 'quick'">
      <!-- Filtri -->
      <div class="flex flex-wrap gap-3 mb-4 items-end">
        <div class="flex flex-col gap-1">
          <label class="text-xs text-gray-500 font-medium">Profilo</label>
          <select
            v-model="filters.profile_type"
            class="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
            @change="loadQuick"
          >
            <option value="">Tutti i profili</option>
            <option value="FOREIGN">🌍 Estero</option>
            <option value="PRIVATE">👤 Privato</option>
            <option value="BUSINESS">🏢 Azienda</option>
          </select>
        </div>

        <div class="flex flex-col gap-1 flex-1 min-w-48">
          <label class="text-xs text-gray-500 font-medium">Cerca</label>
          <input
            v-model="filters.q"
            type="text"
            placeholder="Nome, cognome o email..."
            class="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
            @keyup.enter="loadQuick"
          />
        </div>

        <div class="flex items-center gap-2 pb-2">
          <label class="flex items-center gap-2 cursor-pointer select-none text-sm text-gray-700 dark:text-gray-300">
            <div
              class="relative w-10 h-5 rounded-full transition-colors duration-200"
              :class="filters.show_blocked ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'"
              @click="filters.show_blocked = !filters.show_blocked; loadQuick()"
            >
              <span
                class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200"
                :class="filters.show_blocked ? 'translate-x-5' : 'translate-x-0'"
              ></span>
            </div>
            Mostra anche bloccati
          </label>
        </div>

        <button
          class="flex items-center gap-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition"
          @click="loadQuick"
        >
          <span class="material-icons text-sm">search</span>
          Cerca
        </button>
      </div>

      <!-- Info risultati -->
      <div class="flex items-center justify-between mb-3">
        <p class="text-sm text-gray-500 dark:text-gray-400">
          <span v-if="loading">Caricamento...</span>
          <span v-else>{{ quickTotal }} utenti con segnali sospetti</span>
        </p>
        <div class="flex items-center gap-2 text-sm text-gray-500">
          <span>Ordina per:</span>
          <button
            :class="quickSortBy === 'unpaid_eur' ? 'text-blue-600 font-medium' : 'hover:text-blue-500'"
            @click="quickSortBy = 'unpaid_eur'; loadQuick()"
          >
            Non pagato
          </button>
          <span>·</span>
          <button
            :class="quickSortBy === 'created_at' ? 'text-blue-600 font-medium' : 'hover:text-blue-500'"
            @click="quickSortBy = 'created_at'; loadQuick()"
          >
            Più recenti
          </button>
        </div>
      </div>

      <!-- Skeleton -->
      <div v-if="loading" class="space-y-2">
        <div v-for="i in 8" :key="i" class="h-16 bg-gray-100 dark:bg-gray-800 rounded-xl animate-pulse"></div>
      </div>

      <!-- Tabella quick -->
      <div v-else-if="quickItems.length" class="bg-white dark:bg-gray-900 rounded-xl shadow overflow-hidden">
        <table class="w-full text-sm">
          <thead>
            <tr class="bg-gray-50 dark:bg-gray-800 text-left text-xs text-gray-500 uppercase tracking-wide">
              <th class="px-4 py-3">Nome</th>
              <th class="px-4 py-3 hidden md:table-cell">Email</th>
              <th class="px-4 py-3 hidden lg:table-cell w-24">Profilo</th>
              <th class="px-4 py-3 hidden lg:table-cell w-24">Iscritto</th>
              <th class="px-4 py-3 w-28 text-right">Non pagato</th>
              <th class="px-4 py-3 hidden xl:table-cell">Segnali</th>
              <th class="px-4 py-3 w-28 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-800">
            <tr
              v-for="user in quickItems"
              :key="user.uid"
              class="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition cursor-pointer"
              @click="goToDetail(user.uid)"
            >
              <td class="px-4 py-3">
                <div class="flex items-center gap-2">
                  <div>
                    <p class="font-medium text-gray-900 dark:text-white">
                      {{ user.name }} {{ user.surname }}
                      <span class="text-xs text-gray-400 ml-1">#{{ user.uid }}</span>
                    </p>
                    <p class="text-xs text-gray-400 md:hidden">{{ user.email }}</p>
                    <span
                      v-if="user.in_blacklist"
                      class="text-xs bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 px-1.5 py-0.5 rounded"
                    >Bloccato</span>
                  </div>
                </div>
              </td>
              <td class="px-4 py-3 hidden md:table-cell">
                <span class="text-gray-600 dark:text-gray-400">{{ user.email }}</span>
                <span
                  v-if="user.suspicious_email"
                  class="material-icons text-yellow-500 text-sm ml-1 align-middle"
                  title="Email sospetta"
                >warning</span>
              </td>
              <td class="px-4 py-3 hidden lg:table-cell">
                <span class="text-base">{{ profileIcon(user.profile_type) }}</span>
                <span class="text-xs text-gray-500 ml-1">{{ profileLabel(user.profile_type) }}</span>
              </td>
              <td class="px-4 py-3 hidden lg:table-cell text-gray-500 text-xs">
                {{ relativeDate(user.created_at) }}
              </td>
              <td class="px-4 py-3 text-right">
                <span
                  v-if="user.unpaid_eur > 0"
                  class="font-semibold text-red-600 dark:text-red-400"
                >{{ formatEur(user.unpaid_eur) }}</span>
                <span v-else class="text-gray-300">—</span>
              </td>
              <td class="px-4 py-3 hidden xl:table-cell">
                <div class="flex flex-wrap gap-1">
                  <span
                    v-for="flag in user.flags"
                    :key="flag.code"
                    class="text-xs px-2 py-0.5 rounded-full"
                    :class="flagBadgeClass(flag.code)"
                  >{{ flag.label }}</span>
                </div>
              </td>
              <td class="px-4 py-3 text-right" @click.stop>
                <div class="flex justify-end gap-2">
                  <button
                    class="p-1.5 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 text-blue-600 transition"
                    title="Dettaglio"
                    @click.stop="goToDetail(user.uid)"
                  >
                    <span class="material-icons text-base">visibility</span>
                  </button>
                  <button
                    v-if="!user.in_blacklist"
                    class="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 text-red-500 transition"
                    title="Blocca"
                    @click.stop="openBlockModal(user)"
                  >
                    <span class="material-icons text-base">block</span>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Empty state -->
      <div
        v-else
        class="bg-white dark:bg-gray-900 rounded-xl shadow p-12 text-center text-gray-400"
      >
        <span class="material-icons text-5xl mb-3 block">verified_user</span>
        <p class="text-lg font-medium">Nessun utente sospetto trovato.</p>
        <p class="text-sm mt-1">Nessun utente ha segnali sospetti attivi con i filtri correnti.</p>
      </div>

      <!-- Paginazione quick -->
      <div v-if="quickTotal > quickPageSize" class="flex items-center justify-between mt-4">
        <p class="text-sm text-gray-500">
          Pagina {{ quickPage + 1 }} di {{ quickTotalPages }}
        </p>
        <div class="flex gap-2">
          <button
            class="px-3 py-1.5 rounded border text-sm disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-800 transition"
            :disabled="quickPage === 0"
            @click="quickPage--; loadQuick()"
          >← Precedente</button>
          <button
            class="px-3 py-1.5 rounded border text-sm disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-800 transition"
            :disabled="quickPage >= quickTotalPages - 1"
            @click="quickPage++; loadQuick()"
          >Successiva →</button>
        </div>
      </div>

      <!-- CTA deep scan -->
      <div class="mt-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-5">
        <div class="flex items-start gap-4">
          <span class="material-icons text-blue-600 text-3xl mt-0.5">psychology</span>
          <div class="flex-1">
            <h3 class="font-semibold text-gray-900 dark:text-white">Analisi approfondita</h3>
            <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Calcola lo <strong>score di rischio</strong> (0–100) per ogni utente incrociando carte condivise, storico pagamenti, cluster,
              transazioni fallite e altri segnali. L'operazione potrebbe richiedere qualche secondo.
            </p>
            <button
              class="mt-3 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition flex items-center gap-2"
              :disabled="deepScanLoading"
              @click="switchToDeep"
            >
              <span class="material-icons text-sm" :class="{ 'animate-spin': deepScanLoading }">
                {{ deepScanLoading ? 'hourglass_top' : 'play_arrow' }}
              </span>
              {{ deepScanLoading ? 'Scansione in corso...' : 'Avvia scansione' }}
            </button>
          </div>
        </div>
      </div>
    </template>

    <!-- ═══════════════════════════════════════════════════════════ -->
    <!-- VISTA DEEP (analisi approfondita con score)                -->
    <!-- ═══════════════════════════════════════════════════════════ -->
    <template v-if="activeView === 'deep'">
      <!-- Filtri deep -->
      <div class="flex flex-wrap gap-3 mb-4 items-end">
        <div class="flex flex-col gap-1">
          <label class="text-xs text-gray-500 font-medium">Livello rischio</label>
          <select
            v-model="deepFilters.min_score"
            class="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
            @change="runDeepScan"
          >
            <option value="31">Tutti (Medio + Alto)</option>
            <option value="61">Solo Alto (≥ 61)</option>
            <option value="0">Tutti gli utenti</option>
          </select>
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-xs text-gray-500 font-medium">Profilo</label>
          <select
            v-model="deepFilters.profile_type"
            class="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
            @change="runDeepScan"
          >
            <option value="">Tutti i profili</option>
            <option value="FOREIGN">🌍 Estero</option>
            <option value="PRIVATE">👤 Privato</option>
            <option value="BUSINESS">🏢 Azienda</option>
          </select>
        </div>

        <div class="flex flex-col gap-1 flex-1 min-w-48">
          <label class="text-xs text-gray-500 font-medium">Cerca</label>
          <input
            v-model="deepFilters.q"
            type="text"
            placeholder="Nome, cognome o email..."
            class="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
            @keyup.enter="runDeepScan"
          />
        </div>

        <button
          class="flex items-center gap-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm transition"
          :disabled="deepScanLoading"
          @click="runDeepScan"
        >
          <span class="material-icons text-sm" :class="{ 'animate-spin': deepScanLoading }">
            {{ deepScanLoading ? 'hourglass_top' : 'psychology' }}
          </span>
          {{ deepScanLoading ? 'Scansione...' : 'Aggiorna scansione' }}
        </button>
      </div>

      <!-- Deep scan in corso -->
      <div v-if="deepScanLoading" class="space-y-2">
        <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 mb-4">
          <div class="flex items-center gap-3">
            <span class="material-icons text-blue-600 animate-spin">hourglass_top</span>
            <div>
              <p class="font-medium text-gray-900 dark:text-white">Scansione in corso...</p>
              <p class="text-sm text-gray-500">Sto analizzando carte condivise, storico transazioni, cluster e altri segnali. Potrebbe richiedere qualche secondo.</p>
            </div>
          </div>
        </div>
        <div v-for="i in 6" :key="i" class="h-16 bg-gray-100 dark:bg-gray-800 rounded-xl animate-pulse"></div>
      </div>

      <!-- Risultati deep scan non ancora disponibili -->
      <div v-else-if="!deepScanResult" class="bg-white dark:bg-gray-900 rounded-xl shadow p-12 text-center text-gray-400">
        <span class="material-icons text-5xl mb-3 block">psychology</span>
        <p class="text-lg font-medium">Premi "Aggiorna scansione" per avviare l'analisi.</p>
        <p class="text-sm mt-1">Verrà calcolato uno score di rischio per ciascun utente sospetto.</p>
      </div>

      <!-- Risultati deep scan -->
      <template v-else>
        <div class="flex items-center justify-between mb-3">
          <p class="text-sm text-gray-500 dark:text-gray-400">
            {{ deepScanResult.total }} utenti con score ≥ {{ deepFilters.min_score }}
          </p>
        </div>

        <div v-if="deepScanResult.items.length" class="bg-white dark:bg-gray-900 rounded-xl shadow overflow-hidden">
          <table class="w-full text-sm">
            <thead>
              <tr class="bg-gray-50 dark:bg-gray-800 text-left text-xs text-gray-500 uppercase tracking-wide">
                <th class="px-4 py-3 w-16">Rischio</th>
                <th class="px-4 py-3">Nome</th>
                <th class="px-4 py-3 hidden md:table-cell">Email</th>
                <th class="px-4 py-3 hidden lg:table-cell w-24">Profilo</th>
                <th class="px-4 py-3 hidden lg:table-cell w-24">Iscritto</th>
                <th class="px-4 py-3 w-28 text-right">Non pagato</th>
                <th class="px-4 py-3 hidden xl:table-cell">Segnali</th>
                <th class="px-4 py-3 w-28 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 dark:divide-gray-800">
              <tr
                v-for="user in deepScanResult.items"
                :key="user.uid"
                class="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition cursor-pointer"
                @click="goToDetail(user.uid)"
              >
                <td class="px-4 py-3">
                  <div class="relative group">
                    <span
                      :class="scoreBadgeClass(user.score)"
                      class="inline-flex w-10 h-10 items-center justify-center rounded-full text-sm font-bold cursor-help"
                    >{{ user.score }}</span>
                    <div
                      class="absolute left-12 top-0 z-50 hidden group-hover:block w-72 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl p-3 text-xs"
                    >
                      <p class="font-bold text-gray-800 dark:text-white mb-2">
                        Rischio {{ user.score }}/100 — {{ levelLabel(user.level) }}
                      </p>
                      <ul class="space-y-1">
                        <li
                          v-for="sig in user.signals"
                          :key="sig.code"
                          class="flex justify-between"
                          :class="sig.points > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'"
                        >
                          <span>{{ sig.points > 0 ? '▲' : '▼' }} {{ sig.label }}</span>
                          <span class="font-mono ml-2">{{ sig.points > 0 ? '+' : '' }}{{ sig.points }}</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </td>
                <td class="px-4 py-3">
                  <p class="font-medium text-gray-900 dark:text-white">
                    {{ user.name }} {{ user.surname }}
                  </p>
                  <span
                    v-if="user.in_blacklist"
                    class="text-xs bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 px-1.5 py-0.5 rounded"
                  >Bloccato</span>
                </td>
                <td class="px-4 py-3 hidden md:table-cell text-gray-600 dark:text-gray-400">
                  {{ user.email }}
                </td>
                <td class="px-4 py-3 hidden lg:table-cell">
                  <span class="text-base">{{ profileIcon(user.profile_type) }}</span>
                  <span class="text-xs text-gray-500 ml-1">{{ profileLabel(user.profile_type) }}</span>
                </td>
                <td class="px-4 py-3 hidden lg:table-cell text-gray-500 text-xs">
                  {{ relativeDate(user.created_at) }}
                </td>
                <td class="px-4 py-3 text-right">
                  <span
                    v-if="user.unpaid_eur > 0"
                    class="font-semibold text-red-600 dark:text-red-400"
                  >{{ formatEur(user.unpaid_eur) }}</span>
                  <span v-else class="text-gray-300">—</span>
                </td>
                <td class="px-4 py-3 hidden xl:table-cell">
                  <div class="flex flex-wrap gap-1">
                    <span
                      v-for="sig in activePositiveSignals(user.signals).slice(0, 3)"
                      :key="sig.code"
                      class="text-xs bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300 px-2 py-0.5 rounded-full"
                    >{{ sig.label }}</span>
                  </div>
                </td>
                <td class="px-4 py-3 text-right" @click.stop>
                  <div class="flex justify-end gap-2">
                    <button
                      class="p-1.5 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 text-blue-600 transition"
                      title="Dettaglio"
                      @click.stop="goToDetail(user.uid)"
                    >
                      <span class="material-icons text-base">visibility</span>
                    </button>
                    <button
                      v-if="!user.in_blacklist"
                      class="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 text-red-500 transition"
                      title="Blocca"
                      @click.stop="openBlockModal(user)"
                    >
                      <span class="material-icons text-base">block</span>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="bg-white dark:bg-gray-900 rounded-xl shadow p-12 text-center text-gray-400">
          <span class="material-icons text-5xl mb-3 block">verified_user</span>
          <p class="text-lg font-medium">Nessun utente trovato con questi filtri.</p>
          <p class="text-sm mt-1">Prova ad abbassare la soglia di rischio.</p>
        </div>

        <!-- Paginazione deep -->
        <div v-if="deepScanResult.total > deepPageSize" class="flex items-center justify-between mt-4">
          <p class="text-sm text-gray-500">
            Pagina {{ deepPage + 1 }} di {{ deepTotalPages }}
          </p>
          <div class="flex gap-2">
            <button
              class="px-3 py-1.5 rounded border text-sm disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-800 transition"
              :disabled="deepPage === 0"
              @click="deepPage--; runDeepScan()"
            >← Precedente</button>
            <button
              class="px-3 py-1.5 rounded border text-sm disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-800 transition"
              :disabled="deepPage >= deepTotalPages - 1"
              @click="deepPage++; runDeepScan()"
            >Successiva →</button>
          </div>
        </div>
      </template>
    </template>

    <!-- ═══════════════════════════════════════════════════════════ -->
    <!-- Modal blocco rapido (condiviso tra le due viste)           -->
    <!-- ═══════════════════════════════════════════════════════════ -->
    <flow-modal v-if="blockModal.open" @backdrop="closeBlockModal">
      <template #header>
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
          Blocca {{ blockModal.user?.name }} {{ blockModal.user?.surname }}
        </h3>
      </template>
      <template #body>
        <p class="text-sm text-gray-600 dark:text-gray-400 mb-3">
          Inserisci il motivo del blocco (minimo 5 caratteri).
        </p>
        <textarea
          v-model="blockModal.reason"
          class="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-3 text-sm bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-red-500 focus:outline-none"
          rows="3"
          placeholder="Es: Carta condivisa con 4 account, zero pagamenti..."
        ></textarea>
      </template>
      <template #footer>
        <button
          class="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800 transition"
          @click="closeBlockModal"
        >Annulla</button>
        <button
          class="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 transition disabled:opacity-50"
          :disabled="blockModal.reason.length < 5 || blockModal.loading"
          @click="confirmBlock"
        >{{ blockModal.loading ? 'Blocco in corso...' : 'Blocca utente' }}</button>
      </template>
    </flow-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  data() {
    return {
      activeView: 'quick',
      // Quick list state
      filters: { profile_type: '', q: '', show_blocked: false },
      quickSortBy: 'unpaid_eur',
      quickPage: 0,
      quickPageSize: 50,
      // Deep scan state
      deepFilters: { min_score: '31', profile_type: '', q: '' },
      deepPage: 0,
      deepPageSize: 50,
      // Block modal
      blockModal: { open: false, user: null, reason: '', loading: false },
    };
  },
  computed: {
    ...mapGetters([
      'antifrode_usersAtRiskQuick',
      'antifrode_deepScanResult',
      'antifrode_deepScanLoading',
      'antifrode_loading',
    ]),
    quickItems() {
      return this.antifrode_usersAtRiskQuick?.items || [];
    },
    quickTotal() {
      return this.antifrode_usersAtRiskQuick?.total || 0;
    },
    quickTotalPages() {
      return Math.max(1, Math.ceil(this.quickTotal / this.quickPageSize));
    },
    deepScanResult() {
      return this.antifrode_deepScanResult;
    },
    deepScanLoading() {
      return this.antifrode_deepScanLoading;
    },
    deepTotalPages() {
      return Math.max(1, Math.ceil((this.deepScanResult?.total || 0) / this.deepPageSize));
    },
    loading() {
      return this.antifrode_loading;
    },
  },
  mounted() {
    this.loadQuick();
  },
  methods: {
    loadQuick() {
      this.$store.dispatch('fetchAntifrodeUsersAtRiskQuick', {
        profile_type: this.filters.profile_type,
        q: this.filters.q,
        show_blocked: this.filters.show_blocked ? 'true' : 'false',
        sort_by: this.quickSortBy,
        limit: this.quickPageSize,
        offset: this.quickPage * this.quickPageSize,
      });
    },
    switchToDeep() {
      this.activeView = 'deep';
      if (!this.deepScanResult && !this.deepScanLoading) {
        this.runDeepScan();
      }
    },
    runDeepScan() {
      this.$store.dispatch('fetchAntifrodeDeepScan', {
        min_score: this.deepFilters.min_score,
        profile_type: this.deepFilters.profile_type,
        q: this.deepFilters.q,
        show_blocked: 'false',
        sort_by: 'score',
        limit: this.deepPageSize,
        offset: this.deepPage * this.deepPageSize,
      });
    },
    goToDetail(uid) {
      this.$router.push({ name: 'menu.antifrode.utenti.detail', params: { uid } });
    },
    openBlockModal(user) {
      this.blockModal = { open: true, user, reason: '', loading: false };
    },
    closeBlockModal() {
      this.blockModal = { open: false, user: null, reason: '', loading: false };
    },
    async confirmBlock() {
      this.blockModal.loading = true;
      try {
        await this.$store.dispatch('antifrode_blockUser', {
          uid: this.blockModal.user.uid,
          reason: this.blockModal.reason,
        });
        this.closeBlockModal();
        if (this.activeView === 'quick') this.loadQuick();
        else this.runDeepScan();
      } catch (e) {
        console.error('Blocco fallito', e);
        this.blockModal.loading = false;
      }
    },
    // ── helpers UI ───────────────────────────────────────────────
    scoreBadgeClass(score) {
      if (score >= 70) return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
      if (score >= 40) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300';
      return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
    },
    levelLabel(level) {
      return { HIGH: 'ALTO', MEDIUM: 'MEDIO', LOW: 'BASSO' }[level] || level;
    },
    profileIcon(type) {
      return { FOREIGN: '🌍', BUSINESS: '🏢', PRIVATE: '👤' }[type] || '👤';
    },
    profileLabel(type) {
      return { FOREIGN: 'Estero', BUSINESS: 'Azienda', PRIVATE: 'Privato' }[type] || 'Privato';
    },
    flagBadgeClass(code) {
      if (['S1', 'S2', 'S6'].includes(code)) return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
      if (['S3', 'S4', 'S5'].includes(code)) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300';
      return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300';
    },
    relativeDate(dateStr) {
      if (!dateStr) return '—';
      const diffMs = Date.now() - new Date(dateStr).getTime();
      const days = Math.floor(diffMs / 86400000);
      if (days === 0) return 'oggi';
      if (days === 1) return 'ieri';
      if (days < 30) return `${days} gg fa`;
      if (days < 365) return `${Math.floor(days / 30)} mesi fa`;
      return `${Math.floor(days / 365)} anni fa`;
    },
    formatEur(val) {
      if (!val) return '—';
      return new Intl.NumberFormat('it-IT', {
        style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
      }).format(val);
    },
    activePositiveSignals(signals) {
      return (signals || []).filter((s) => s.points > 0);
    },
  },
};
</script>

<style></style>
